import { UserRoundPlus } from 'lucide-react';

import { useState } from 'react';

import { PlaceholderMemberService } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Alert, Button, Input } from '@splitfiesta/ui';

import { MemberActionRow } from '@/components/members/MemberActionRow';

import { useCreatePlaceholderMutation } from '@/hooks/queries/useGroups';

import { getErrorMessage, handleError } from '@/utils/errorHandler';

export interface AddPlaceholderMemberFormProps {
  groupId: string;
  disabled?: boolean;
  disabledReason?: string;
  /** Controlled expansion, for callers that open the form from elsewhere. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Collapsed roster row that expands into a one-field form for adding a member
 * who has no account. Online-only: the caller passes `disabled` when offline.
 */
export function AddPlaceholderMemberForm({
  groupId,
  disabled = false,
  disabledReason,
  open: openProp,
  onOpenChange,
}: AddPlaceholderMemberFormProps) {
  const { t } = useTranslation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const createPlaceholder = useCreatePlaceholderMutation();

  useNamespace('groups');

  const close = () => {
    setOpen(false);
    setName('');
    setError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Offline the mutation would be paused and silently replayed on
    // reconnect, which is exactly the queueing placeholders must not do.
    if (disabled) {
      return;
    }

    const { valid, trimmed } = PlaceholderMemberService.validateName(name);
    if (!valid || createPlaceholder.isPending) {
      return;
    }

    setError('');

    try {
      await createPlaceholder.mutateAsync({ groupId, name: trimmed });
      setOpen(false);
      setName('');
    } catch (err: unknown) {
      setError(getErrorMessage(handleError(err, 'groups'), t));
    }
  };

  if (!open) {
    return (
      <MemberActionRow
        icon={UserRoundPlus}
        label={t('groups:placeholderMembers.addCta')}
        description={t('groups:placeholderMembers.addDescription')}
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        testId="add-placeholder-cta"
        expanded={open}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 px-6 py-4"
      data-testid="add-placeholder-form"
    >
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t('groups:placeholderMembers.addTitle')}
      </h3>
      <Input
        label={t('groups:placeholderMembers.nameLabel')}
        placeholder={t('groups:placeholderMembers.namePlaceholder')}
        value={name}
        onChange={event => setName(event.target.value)}
        data-testid="placeholder-name-input"
        maxLength={50}
        autoFocus
      />
      <p
        className="text-xs text-gray-500 dark:text-gray-400"
        data-testid="add-placeholder-hint"
      >
        {t('groups:placeholderMembers.addHint')}
      </p>
      {error && (
        <Alert
          type="error"
          message={error}
          data-testid="add-placeholder-error"
        />
      )}
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={disabled || createPlaceholder.isPending}
          title={disabled ? disabledReason : undefined}
          data-testid="add-placeholder-submit"
        >
          {t('groups:placeholderMembers.addSubmit')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={close}
          data-testid="add-placeholder-cancel"
        >
          {t('groups:placeholderMembers.cancel')}
        </Button>
      </div>
    </form>
  );
}

export default AddPlaceholderMemberForm;
